import { c as _c } from "react/compiler-runtime";
// «Биржа товаров» партнёрского кабинета: товары, открытые продавцами для
// продвижения (каталог программ, GET /partner/catalog). Фильтры: поиск,
// категория, минимальный процент, сортировка (по вознаграждению, по
// конверсии, новые). У арбитражника на карточке видна конверсия (CR).
// «Сгенерировать артикул» - доступ open/approved; «Запросить доступ» -
// requestable; pending/rejected/blocked - статус заявки.
import { UGC_TEXTS } from "@osmix/contracts/ugc-articles";
import { Skeleton } from "@osmix/ui/skeleton";
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { api, type Category, type UgcCatalogItem, type UgcCatalogQuery } from "@/api";
import { ErrorState } from "@/components/common/states";
import { SearchIcon } from "@/components/icons";
import { GenerateDialog } from "@/components/partner/generate-dialog";
import { EmptyNote, fmtPercent, partnerKey } from "@/components/partner/partner-ui";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/text-field";
import { useFormatPrice } from "@/lib/use-format-price";
import { cn } from "@/lib/utils";
import { usePartner } from "./shell";
const PAGE_SIZE = 24;
const MIN_OPTIONS = ["0", "5", "10", "15", "20"] as const;
const SORTS: UgcCatalogQuery["sort"][] = ["commission", "cr", "new"];

/** Корни дерева и их прямые дети - для выпадающего списка категорий. */
const flatCategories = (list: Category[]): Array<{
  id: string;
  name: string;
  depth: number;
}> => list.flatMap(root => [{
  id: root.id,
  name: root.name,
  depth: 0
}, ...(root.children ?? []).map(c => ({
  id: c.id,
  name: c.name,
  depth: 1
}))]);
export function PartnerCatalogPage() {
  const $ = _c(109);
  const {
    t
  } = useTranslation();
  const {
    partner,
    userId
  } = usePartner();
  const [q, setQ] = useState("");
  const [query, setQuery] = useState("");
  const [categoryId, setCategoryId] = useState("all");
  const [minCommission, setMinCommission] = useState("0");
  const [sort, setSort] = useState("commission");
  const [generateFor, setGenerateFor] = useState(null);
  const arbitrage = partner.role === "arbitrage";
  let t0;
  let t1;
  if ($[0] !== q) {
    t0 = () => {
      const id = window.setTimeout(() => setQuery(q.trim()), 300);
      return () => window.clearTimeout(id);
    };
    t1 = [q];
    $[0] = q;
    $[1] = t0;
    $[2] = t1;
  } else {
    t0 = $[1];
    t1 = $[2];
  }
  useEffect(t0, t1);
  let t2;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      queryKey: ["categories"],
      queryFn: _temp,
      staleTime: 600000
    };
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const categories = useQuery(t2);
  const t3 = query || undefined;
  const t4 = categoryId === "all" ? undefined : categoryId;
  const t5 = Number(minCommission) || undefined;
  let t6;
  let t7;
  let t8;
  if ($[4] !== sort || $[5] !== t3 || $[6] !== t4 || $[7] !== t5 || $[8] !== userId) {
    const filter = {
      q: t3,
      categoryId: t4,
      minCommission: t5,
      sort
    };
    t7 = useInfiniteQuery;
    t6 = partnerKey(userId, "catalog", filter);
    t8 = t9 => {
      const {
        pageParam
      } = t9;
      return api.ugc.partner.catalog({
        ...filter,
        page: pageParam,
        pageSize: PAGE_SIZE
      });
    };
    $[4] = sort;
    $[5] = t3;
    $[6] = t4;
    $[7] = t5;
    $[8] = userId;
    $[9] = t6;
    $[10] = t7;
    $[11] = t8;
  } else {
    t6 = $[9];
    t7 = $[10];
    t8 = $[11];
  }
  let t9;
  if ($[12] !== t6 || $[13] !== t8) {
    t9 = {
      queryKey: t6,
      queryFn: t8,
      initialPageParam: 1,
      getNextPageParam: _temp3
    };
    $[12] = t6;
    $[13] = t8;
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  const catalog = t7(t9);
  let t10;
  let t11;
  let t12;
  let t13;
  let t14;
  if ($[15] !== arbitrage || $[16] !== catalog || $[17] !== categories.data || $[18] !== categoryId || $[19] !== minCommission || $[20] !== q || $[21] !== sort || $[22] !== t) {
    const items = (catalog.data?.pages ?? []).flatMap(_temp4);
    const total = catalog.data?.pages[0]?.total ?? 0;
    t10 = "flex min-w-0 flex-col gap-4";
    t11 = "partner-catalog";
    let t15;
    if ($[28] === Symbol.for("react.memo_cache_sentinel")) {
      t15 = e => setQ(e.target.value);
      $[28] = t15;
    } else {
      t15 = $[28];
    }
    let t16;
    if ($[29] !== t) {
      t16 = t("partner.catalog.search");
      $[29] = t;
      $[30] = t16;
    } else {
      t16 = $[30];
    }
    let t17;
    if ($[31] !== t) {
      t17 = t("partner.catalog.search");
      $[31] = t;
      $[32] = t17;
    } else {
      t17 = $[32];
    }
    let t18;
    if ($[33] === Symbol.for("react.memo_cache_sentinel")) {
      t18 = <SearchIcon size={16} />;
      $[33] = t18;
    } else {
      t18 = $[33];
    }
    let t19;
    if ($[34] !== q || $[35] !== t16 || $[36] !== t17) {
      t19 = <Input type="search" value={q} onChange={t15} placeholder={t16} aria-label={t17} start={t18} fieldClassName="col-span-2 lg:col-span-1" data-testid="partner-catalog-search" />;
      $[34] = q;
      $[35] = t16;
      $[36] = t17;
      $[37] = t19;
    } else {
      t19 = $[37];
    }
    let t20;
    if ($[38] !== t) {
      t20 = t("partner.catalog.category");
      $[38] = t;
      $[39] = t20;
    } else {
      t20 = $[39];
    }
    let t21;
    if ($[40] === Symbol.for("react.memo_cache_sentinel")) {
      t21 = <SelectValue />;
      $[40] = t21;
    } else {
      t21 = $[40];
    }
    let t22;
    if ($[41] !== t20) {
      t22 = <SelectTrigger className="col-span-2 w-full min-w-0 lg:col-span-1" aria-label={t20}>{t21}</SelectTrigger>;
      $[41] = t20;
      $[42] = t22;
    } else {
      t22 = $[42];
    }
    let t23;
    if ($[43] !== t) {
      t23 = t("partner.catalog.allCategories");
      $[43] = t;
      $[44] = t23;
    } else {
      t23 = $[44];
    }
    let t24;
    if ($[45] !== t23) {
      t24 = <SelectItem value="all">{t23}</SelectItem>;
      $[45] = t23;
      $[46] = t24;
    } else {
      t24 = $[46];
    }
    let t25;
    if ($[47] !== categories.data) {
      t25 = flatCategories(categories.data ?? []).map(_temp5);
      $[47] = categories.data;
      $[48] = t25;
    } else {
      t25 = $[48];
    }
    let t26;
    if ($[49] !== t24 || $[50] !== t25) {
      t26 = <SelectContent>{t24}{t25}</SelectContent>;
      $[49] = t24;
      $[50] = t25;
      $[51] = t26;
    } else {
      t26 = $[51];
    }
    let t27;
    if ($[52] !== categoryId || $[53] !== t22 || $[54] !== t26) {
      t27 = <Select value={categoryId} onValueChange={setCategoryId}>{t22}{t26}</Select>;
      $[52] = categoryId;
      $[53] = t22;
      $[54] = t26;
      $[55] = t27;
    } else {
      t27 = $[55];
    }
    let t28;
    if ($[56] !== t) {
      t28 = t("partner.catalog.minCommission");
      $[56] = t;
      $[57] = t28;
    } else {
      t28 = $[57];
    }
    let t29;
    if ($[58] === Symbol.for("react.memo_cache_sentinel")) {
      t29 = <SelectValue />;
      $[58] = t29;
    } else {
      t29 = $[58];
    }
    let t30;
    if ($[59] !== t28) {
      t30 = <SelectTrigger className="w-full min-w-0" aria-label={t28}>{t29}</SelectTrigger>;
      $[59] = t28;
      $[60] = t30;
    } else {
      t30 = $[60];
    }
    let t31;
    if ($[61] !== t) {
      t31 = MIN_OPTIONS.map(v => <SelectItem key={v} value={v}>{v === "0" ? t("partner.catalog.anyCommission") : t("partner.catalog.fromPercent", {
          value: v
        })}</SelectItem>);
      $[61] = t;
      $[62] = t31;
    } else {
      t31 = $[62];
    }
    let t32;
    if ($[63] !== t31) {
      t32 = <SelectContent>{t31}</SelectContent>;
      $[63] = t31;
      $[64] = t32;
    } else {
      t32 = $[64];
    }
    let t33;
    if ($[65] !== minCommission || $[66] !== t30 || $[67] !== t32) {
      t33 = <Select value={minCommission} onValueChange={setMinCommission}>{t30}{t32}</Select>;
      $[65] = minCommission;
      $[66] = t30;
      $[67] = t32;
      $[68] = t33;
    } else {
      t33 = $[68];
    }
    let t34;
    if ($[69] === Symbol.for("react.memo_cache_sentinel")) {
      t34 = v_0 => setSort(v_0 as UgcCatalogQuery["sort"]);
      $[69] = t34;
    } else {
      t34 = $[69];
    }
    let t35;
    if ($[70] !== t) {
      t35 = t("partner.catalog.sortLabel");
      $[70] = t;
      $[71] = t35;
    } else {
      t35 = $[71];
    }
    let t36;
    if ($[72] === Symbol.for("react.memo_cache_sentinel")) {
      t36 = <SelectValue />;
      $[72] = t36;
    } else {
      t36 = $[72];
    }
    let t37;
    if ($[73] !== t35) {
      t37 = <SelectTrigger className="w-full min-w-0" aria-label={t35}>{t36}</SelectTrigger>;
      $[73] = t35;
      $[74] = t37;
    } else {
      t37 = $[74];
    }
    let t38;
    if ($[75] !== t) {
      t38 = SORTS.map(s => <SelectItem key={s} value={s}>{t(`partner.catalog.sort.${s}`)}</SelectItem>);
      $[75] = t;
      $[76] = t38;
    } else {
      t38 = $[76];
    }
    let t39;
    if ($[77] !== t38) {
      t39 = <SelectContent>{t38}</SelectContent>;
      $[77] = t38;
      $[78] = t39;
    } else {
      t39 = $[78];
    }
    let t40;
    if ($[79] !== sort || $[80] !== t37 || $[81] !== t39) {
      t40 = <Select value={sort} onValueChange={t34}>{t37}{t39}</Select>;
      $[79] = sort;
      $[80] = t37;
      $[81] = t39;
      $[82] = t40;
    } else {
      t40 = $[82];
    }
    if ($[83] !== t19 || $[84] !== t27 || $[85] !== t33 || $[86] !== t40) {
      t12 = <div className="grid grid-cols-2 gap-2 lg:grid-cols-[minmax(0,2fr)_minmax(0,1.3fr)_minmax(0,1fr)_minmax(0,1.2fr)]">{t19}{t27}{t33}{t40}</div>;
      $[83] = t19;
      $[84] = t27;
      $[85] = t33;
      $[86] = t40;
      $[87] = t12;
    } else {
      t12 = $[87];
    }
    if ($[88] !== catalog.error || $[89] !== catalog.isPending || $[90] !== t || $[91] !== total) {
      t13 = !catalog.isPending && !catalog.error && <p className="text-[14px] leading-[18px] text-ds-secondary">{t("partner.catalog.total", {
          count: total
        })}</p>;
      $[88] = catalog.error;
      $[89] = catalog.isPending;
      $[90] = t;
      $[91] = total;
      $[92] = t13;
    } else {
      t13 = $[92];
    }
    t14 = catalog.isPending ? <div className="grid grid-cols-1 gap-3 min-[480px]:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">{Array.from({
        length: 8
      }, _temp6)}</div> : catalog.error ? <ErrorState onRetry={() => void catalog.refetch()} /> : items.length === 0 ? <EmptyNote>{t("partner.catalog.empty")}</EmptyNote> : <div className="grid grid-cols-1 gap-3 min-[480px]:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">{items.map(item => <CatalogCard key={item.productId} item={item} arbitrage={arbitrage} onGenerate={() => setGenerateFor(item)} />)}</div>;
    $[15] = arbitrage;
    $[16] = catalog;
    $[17] = categories.data;
    $[18] = categoryId;
    $[19] = minCommission;
    $[20] = q;
    $[21] = sort;
    $[22] = t;
    $[23] = t10;
    $[24] = t11;
    $[25] = t12;
    $[26] = t13;
    $[27] = t14;
  } else {
    t10 = $[23];
    t11 = $[24];
    t12 = $[25];
    t13 = $[26];
    t14 = $[27];
  }
  let t15;
  if ($[93] !== catalog || $[94] !== t) {
    t15 = catalog.hasNextPage && <Button variant="gray" size="md" className="self-center" disabled={catalog.isFetchingNextPage} onClick={() => void catalog.fetchNextPage()}>{t("partner.catalog.more")}</Button>;
    $[93] = catalog;
    $[94] = t;
    $[95] = t15;
  } else {
    t15 = $[95];
  }
  let t16;
  if ($[96] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = open => {
      if (!open) {
        setGenerateFor(null);
      }
    };
    $[96] = t16;
  } else {
    t16 = $[96];
  }
  let t17;
  if ($[97] !== generateFor || $[98] !== partner.role || $[99] !== userId) {
    t17 = <GenerateDialog item={generateFor} role={partner.role} userId={userId} onOpenChange={t16} />;
    $[97] = generateFor;
    $[98] = partner.role;
    $[99] = userId;
    $[100] = t17;
  } else {
    t17 = $[100];
  }
  let t18;
  if ($[101] !== t10 || $[102] !== t11 || $[103] !== t12 || $[104] !== t13 || $[105] !== t14 || $[106] !== t15 || $[107] !== t17) {
    t18 = <section className={t10} data-testid={t11}>{t12}{t13}{t14}{t15}{t17}</section>;
    $[101] = t10;
    $[102] = t11;
    $[103] = t12;
    $[104] = t13;
    $[105] = t14;
    $[106] = t15;
    $[107] = t17;
    $[108] = t18;
  } else {
    t18 = $[108];
  }
  return t18;
}
function _temp6(_, i) {
  return <Skeleton key={i} className="h-[340px] rounded-[20px]" />;
}
function _temp5(c) {
  return <SelectItem key={c.id} value={c.id} className={c.depth ? "pl-6" : undefined}>{c.name}</SelectItem>;
}
function _temp4(p_0) {
  return p_0.items;
}
function _temp3(last, pages) {
  return pages.reduce(_temp2, 0) < last.total && last.items.length > 0 ? pages.length + 1 : undefined;
}
function _temp2(n, p) {
  return n + p.items.length;
}
function _temp() {
  return api.categories.list();
}
function CatalogCard(t0) {
  const $ = _c(66);
  const {
    item,
    arbitrage,
    onGenerate
  } = t0;
  const {
    t
  } = useTranslation();
  const fmt = useFormatPrice();
  const {
    userId
  } = usePartner();
  const qc = useQueryClient();
  let t1;
  if ($[0] !== item.productId) {
    t1 = () => api.ugc.partner.requestAccess(item.productId);
    $[0] = item.productId;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== qc || $[3] !== t || $[4] !== userId) {
    t2 = () => {
      toast.success(t("partner.catalog.requested"));
      qc.invalidateQueries({
        queryKey: partnerKey(userId, "catalog")
      });
    };
    $[2] = qc;
    $[3] = t;
    $[4] = userId;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== t) {
    t3 = e => toast.error(e instanceof Error ? e.message : t("common.error"));
    $[6] = t;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== t1 || $[9] !== t2 || $[10] !== t3) {
    t4 = {
      mutationFn: t1,
      onSuccess: t2,
      onError: t3
    };
    $[8] = t1;
    $[9] = t2;
    $[10] = t3;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  const request = useMutation(t4);
  const canGenerate = item.access === "open" || item.access === "approved";
  let t5;
  if ($[12] !== item.productImage || $[13] !== item.productTitle) {
    t5 = item.productImage && <img src={item.productImage} alt={item.productTitle} loading="lazy" className="size-full object-cover" />;
    $[12] = item.productImage;
    $[13] = item.productTitle;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  let t6;
  if ($[15] !== item.commissionPercent) {
    t6 = fmtPercent(item.commissionPercent);
    $[15] = item.commissionPercent;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  let t7;
  if ($[17] !== t6) {
    t7 = <span className="absolute top-2 left-2 inline-flex h-7 items-center rounded-full bg-ds-accent px-2.5 text-[13px] leading-4 font-bold text-white">{t6}</span>;
    $[17] = t6;
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  let t8;
  if ($[19] !== arbitrage || $[20] !== item.cr || $[21] !== t) {
    t8 = arbitrage && <span className="absolute top-2 right-2 inline-flex h-7 items-center rounded-full bg-ds-primary/80 px-2.5 text-[12px] leading-4 font-semibold text-white" title={t("partner.catalog.crHint")}>CR {fmtPercent(item.cr)}</span>;
    $[19] = arbitrage;
    $[20] = item.cr;
    $[21] = t;
    $[22] = t8;
  } else {
    t8 = $[22];
  }
  let t9;
  if ($[23] !== t5 || $[24] !== t7 || $[25] !== t8) {
    t9 = <div className="relative aspect-[4/3] bg-white">{t5}{t7}{t8}</div>;
    $[23] = t5;
    $[24] = t7;
    $[25] = t8;
    $[26] = t9;
  } else {
    t9 = $[26];
  }
  let t10;
  if ($[27] !== item.productTitle) {
    t10 = <h3 className="line-clamp-2 text-[14px] leading-[18px] font-semibold text-ds-ink">{item.productTitle}</h3>;
    $[27] = item.productTitle;
    $[28] = t10;
  } else {
    t10 = $[28];
  }
  const t11 = item.categoryName ? ` · ${item.categoryName}` : "";
  let t12;
  if ($[29] !== item.shopName || $[30] !== t11) {
    t12 = <p className="truncate text-[12px] leading-4 text-ds-secondary">{item.shopName}{t11}</p>;
    $[29] = item.shopName;
    $[30] = t11;
    $[31] = t12;
  } else {
    t12 = $[31];
  }
  let t13;
  if ($[32] !== t10 || $[33] !== t12) {
    t13 = <div className="flex min-w-0 flex-col gap-0.5">{t10}{t12}</div>;
    $[32] = t10;
    $[33] = t12;
    $[34] = t13;
  } else {
    t13 = $[34];
  }
  let t14;
  if ($[35] !== fmt || $[36] !== item.price) {
    t14 = fmt(item.price);
    $[35] = fmt;
    $[36] = item.price;
    $[37] = t14;
  } else {
    t14 = $[37];
  }
  let t15;
  if ($[38] !== t14) {
    t15 = <span className="text-[16px] leading-5 font-bold text-ds-primary">{t14}</span>;
    $[38] = t14;
    $[39] = t15;
  } else {
    t15 = $[39];
  }
  const t16 = item.price * item.commissionPercent / 100;
  let t17;
  if ($[40] !== fmt || $[41] !== t || $[42] !== t16) {
    t17 = t("partner.catalog.rewardValue", {
      value: fmt(t16)
    });
    $[40] = fmt;
    $[41] = t;
    $[42] = t16;
    $[43] = t17;
  } else {
    t17 = $[43];
  }
  let t18;
  if ($[44] !== t17) {
    t18 = <span className="text-[12px] leading-4 text-ds-secondary">{t17}</span>;
    $[44] = t17;
    $[45] = t18;
  } else {
    t18 = $[45];
  }
  let t19;
  if ($[46] !== t15 || $[47] !== t18) {
    t19 = <div className="flex items-baseline justify-between gap-2">{t15}{t18}</div>;
    $[46] = t15;
    $[47] = t18;
    $[48] = t19;
  } else {
    t19 = $[48];
  }
  let t20;
  if ($[49] !== item.myArticles || $[50] !== t) {
    t20 = item.myArticles > 0 && <p className="text-[12px] leading-4 font-medium text-ds-secondary">{t("partner.catalog.myArticles", {
        count: item.myArticles
      })}</p>;
    $[49] = item.myArticles;
    $[50] = t;
    $[51] = t20;
  } else {
    t20 = $[51];
  }
  let t21;
  if ($[52] !== canGenerate || $[53] !== item.access || $[54] !== onGenerate || $[55] !== request || $[56] !== t) {
    t21 = <div className="mt-auto flex flex-col gap-1.5 pt-1">{canGenerate ? <Button size="md" className="w-full" onClick={onGenerate} data-testid="partner-generate-open">{t("partner.catalog.generate")}</Button> : item.access === "requestable" ? <><p className="text-[12px] leading-4 text-ds-secondary">{t("partner.catalog.noAccess", {
            defaultValue: UGC_TEXTS.noAccess
          })}</p><Button variant="gray" size="md" className="w-full" disabled={request.isPending} onClick={() => request.mutate()} data-testid="partner-request-access">{t("partner.catalog.request")}</Button></> : <p className={cn("rounded-[10px] px-3 py-2.5 text-center text-[13px] leading-[17px] font-medium", item.access === "pending" ? "bg-[#FFF4D6] text-[#8A5A00]" : "bg-ds-gray text-ds-secondary")}>{t(`partner.catalog.access.${item.access}`)}</p>}</div>;
    $[52] = canGenerate;
    $[53] = item.access;
    $[54] = onGenerate;
    $[55] = request;
    $[56] = t;
    $[57] = t21;
  } else {
    t21 = $[57];
  }
  let t22;
  if ($[58] !== t13 || $[59] !== t19 || $[60] !== t20 || $[61] !== t21) {
    t22 = <div className="flex flex-1 flex-col gap-2 p-3">{t13}{t19}{t20}{t21}</div>;
    $[58] = t13;
    $[59] = t19;
    $[60] = t20;
    $[61] = t21;
    $[62] = t22;
  } else {
    t22 = $[62];
  }
  let t23;
  if ($[63] !== t22 || $[64] !== t9) {
    t23 = <article className="flex min-w-0 flex-col overflow-hidden rounded-[20px] bg-ds-quaternary" data-testid="partner-catalog-card">{t9}{t22}</article>;
    $[63] = t22;
    $[64] = t9;
    $[65] = t23;
  } else {
    t23 = $[65];
  }
  return t23;
}